import React from "react";
import { 
  ShieldCheck, Lock, Eye, FileText, 
  Bell, Globe, ArrowRight, ShieldAlert, 
  CheckCircle, Fingerprint 
} from "lucide-react";

const PrivacyPolicy = () => {
  const lastUpdated = "March 28, 2026";

  const sections = [
    {
      icon: <Eye className="w-6 h-6 text-blue-600" />,
      title: "Information We Collect",
      content: "We collect information that you provide directly to us, including your name, email address, phone number, and site address when you request a security audit or purchase our surveillance solutions."
    },
    {
      icon: <Lock className="w-6 h-6 text-amber-500" />,
      title: "How We Use Your Data",
      content: "Your data is used to provide and maintain our security services, process transactions, and send technical notices or support messages. We do not sell your personal data to third parties."
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-green-600" />,
      title: "Security Measures",
      content: "We implement industry-standard encryption and security protocols to protect your personal information. Our systems are monitored 24/7 to prevent unauthorized access or data breaches."
    },
    {
      icon: <Globe className="w-6 h-6 text-purple-600" />,
      title: "Cookies & Tracking",
      content: "We use cookies to enhance your browsing experience and analyze website traffic. You can choose to disable cookies through your browser settings, though some features may function differently."
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* --- HERO SECTION --- */}
      <section className="relative bg-[#0B1F33] pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background Accents */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-blue-600/10 to-transparent pointer-events-none"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-amber-500/5 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="md:w-3/5 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400">
                <ShieldAlert size={16} />
                <span className="text-[10px] font-black uppercase tracking-widest">Trust & Transparency</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-black text-white leading-tight uppercase italic">
                Your Privacy is our <br />
                <span className="text-blue-500 underline decoration-amber-500 underline-offset-8">Primary Mission</span>
              </h1>
              <p className="text-slate-400 text-base max-w-xl leading-relaxed">
                At Suruksha 24/7, security isn't just about cameras and locks—it's about the integrity of your personal data. Learn how we protect your information with enterprise-grade standards.
              </p>
              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center gap-2 text-white/80 text-sm font-bold">
                  <CheckCircle size={18} className="text-amber-500" /> End-to-End Encryption
                </div>
                <div className="flex items-center gap-2 text-white/80 text-sm font-bold">
                  <CheckCircle size={18} className="text-amber-500" /> Zero Data Selling
                </div>
              </div>
            </div>

            <div className="md:w-2/5 flex justify-center">
              <div className="relative group">
                {/* Decorative Rotating Ring */}
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-amber-500 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-700 animate-pulse"></div>
                <div className="relative bg-white/5 border border-white/10 p-10 rounded-[3rem] backdrop-blur-xl flex flex-col items-center">
                  <Fingerprint size={80} className="text-blue-500 mb-4 animate-pulse" />
                  <div className="text-center">
                    <p className="text-white font-black text-2xl tracking-tighter italic uppercase">Secured</p>
                    <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Database Protocol 2.4.0</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- CONTENT SECTION --- */}
      <div className="max-w-4xl mx-auto px-6 -mt-12 relative z-20 pb-20">
        
        {/* Introduction Card */}
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl shadow-slate-200 border border-slate-100 mb-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-8 border-b border-slate-100">
             <div>
                <h2 className="text-2xl font-black text-slate-900 uppercase italic leading-none">Introduction</h2>
                <p className="text-slate-400 text-xs mt-2 font-bold uppercase tracking-widest">Reference: SUR-PRIV-2026</p>
             </div>
             <div className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 text-xs font-bold">
               Last Updated: {lastUpdated}
             </div>
          </div>
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              At <strong className="text-slate-900">Suruksha 24/7</strong>, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or use our security services. By using our services, you consent to the data practices described in this policy.
            </p>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {sections.map((item, idx) => (
            <div key={idx} className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="mb-6 w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 group-hover:bg-white">
                {item.icon}
              </div>
              <h3 className="text-lg font-black text-slate-900 mb-3 uppercase tracking-tight italic">{item.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{item.content}</p>
            </div>
          ))}
        </div>

        {/* Detailed Rights & Policy */}
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-slate-100 space-y-10">
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                <Bell size={20} />
              </div>
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest italic">Your Rights</h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed pl-14">
              You have the right to access, correct, or delete your personal information at any time. If you wish to exercise these rights or have questions regarding your data, please contact our privacy officer at <a href="mailto:support@suruksha247.com" className="text-blue-600 font-bold hover:underline">support@suruksha247.com</a>.
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
                <FileText size={20} />
              </div>
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest italic">Policy Updates</h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed pl-14">
              We may update this policy periodically to reflect changes in our practices or for legal reasons. We will notify you of any significant changes by posting the new policy on this page with a new "Last Updated" date.
            </p>
          </section>

          {/* Contact CTA */}
          <div className="pt-10 border-t border-slate-100 mt-10 text-center">
            <p className="text-slate-400 text-[10px] uppercase font-black tracking-[0.3em] mb-6">Security Compliance Department</p>
            <a 
              href="mailto:contact@suruksha247.com" 
              className="inline-flex items-center justify-center px-10 py-4 bg-[#0B1F33] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-600 transition-all duration-300 gap-3 shadow-xl shadow-blue-900/10"
            >
              Contact Privacy Team <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;